// Wraps pictures in an A4 PDF, one picture per page, so a report can be saved as a PDF without a PDF library.
const PAGE = { width: 595, height: 842 };
const MARGIN = 28;

// Height divided by width of the area a picture can use on a page: a picture this shape fills a whole page.
export const PAGE_RATIO = (PAGE.height - MARGIN * 2) / (PAGE.width - MARGIN * 2);

const ascii = (text) => Uint8Array.from(text, (character) => character.charCodeAt(0));

// A canvas too large for the browser gives back an empty data URL; that must stop the download, not make a broken file.
export function jpegBytesFromDataUrl(dataUrl) {
    if (!/^data:image\/jpeg;base64,.+/.test(dataUrl)) throw new Error('The picture could not be made (it may be too large for this browser).');
    return Uint8Array.from(atob(dataUrl.split(',')[1]), (character) => character.charCodeAt(0));
}

// Returns the bytes of a PDF with one page per picture, each scaled to the page width and placed at the top.
// A picture is { jpeg: Uint8Array, width, height } with the size in pixels.
export function buildImagePdf(pictures) {
    const parts = [];
    const offsets = [];
    let length = 0;
    const push = (bytes) => { parts.push(bytes); length += bytes.length; };
    const object = (number, head, stream) => {
        offsets[number] = length;
        push(ascii(`${number} 0 obj\n${head}\n`));
        if (stream) {
            push(ascii('stream\n'));
            push(stream);
            push(ascii('\nendstream\n'));
        }
        push(ascii('endobj\n'));
    };

    // Objects 1 and 2 are the catalog and the page list; each page then takes three: the page, its picture, its drawing.
    const pageNumber = (index) => 3 + index * 3;
    push(ascii('%PDF-1.4\n'));
    object(1, '<< /Type /Catalog /Pages 2 0 R >>');
    object(2, `<< /Type /Pages /Kids [${pictures.map((_, index) => `${pageNumber(index)} 0 R`).join(' ')}] /Count ${pictures.length} >>`);
    pictures.forEach(({ jpeg, width: pixelWidth, height: pixelHeight }, index) => {
        const page = pageNumber(index);
        const scale = Math.min((PAGE.width - MARGIN * 2) / pixelWidth, (PAGE.height - MARGIN * 2) / pixelHeight);
        const width = pixelWidth * scale;
        const height = pixelHeight * scale;
        const drawing = ascii(`q ${width.toFixed(2)} 0 0 ${height.toFixed(2)} ${MARGIN} ${(PAGE.height - MARGIN - height).toFixed(2)} cm /Im0 Do Q`);
        object(page, `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE.width} ${PAGE.height}] /Resources << /XObject << /Im0 ${page + 1} 0 R >> >> /Contents ${page + 2} 0 R >>`);
        object(page + 1, `<< /Type /XObject /Subtype /Image /Width ${pixelWidth} /Height ${pixelHeight} /ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>`, jpeg);
        object(page + 2, `<< /Length ${drawing.length} >>`, drawing);
    });

    const total = 2 + pictures.length * 3;
    const xrefAt = length;
    let xref = `xref\n0 ${total + 1}\n0000000000 65535 f \n`;
    for (let number = 1; number <= total; number += 1) xref += `${String(offsets[number]).padStart(10, '0')} 00000 n \n`;
    push(ascii(`${xref}trailer\n<< /Size ${total + 1} /Root 1 0 R >>\nstartxref\n${xrefAt}\n%%EOF\n`));

    const pdf = new Uint8Array(length);
    let at = 0;
    parts.forEach((bytes) => { pdf.set(bytes, at); at += bytes.length; });
    return pdf;
}
